import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { type Nullable } from '@direct-frontend/stdlib';
import { persistentStorage } from './persistentStorage';
import { type PersistentStorageKey } from './types';

const makeSubscribe =
    (key: PersistentStorageKey) =>
    (onStoreChange: () => void): (() => void) => {
        persistentStorage.on(key, onStoreChange);

        return () => persistentStorage.off(key, onStoreChange);
    };

const makeGetSnapshot =
    <T extends string>(key: PersistentStorageKey) =>
    (): Nullable<T> =>
        persistentStorage.getItem(key) as Nullable<T>;

const getServerSnapshot = () => null;

export const usePersistentStorage = <T extends string>(key: PersistentStorageKey) => {
    const [subscribe, getSnapshot] = useMemo(() => [makeSubscribe(key), makeGetSnapshot<T>(key)], [key]);
    const value = useSyncExternalStore<Nullable<T>>(subscribe, getSnapshot, getServerSnapshot);
    const setValue = useCallback(
        (newValue: Nullable<T>) => {
            if (newValue === null) {
                persistentStorage.removeItem(key);
            } else {
                persistentStorage.setItem(key, newValue);
            }
        },
        [key]
    );

    return [value, setValue] as const;
};
